import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  CalendarToday,
  People,
  Person,
  ContentCut,
  Payment,
  Settings,
  Logout,
  Assessment,
  Spa,
  Add,
  Home,
  ShoppingCart,
  Store,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [salonName, setSalonName] = useState('Bella Vista');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Fetch salon name from settings
    fetch('/api/settings/salon_name')
      .then(res => res.json())
      .then(data => {
        if (data.value) setSalonName(data.value);
      })
      .catch(() => {});
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const getMenuItems = () => {
    // Define menu categories
    const baseItems = [
      { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
      { text: 'Home', icon: <Home />, path: '/home' },
    ];
    
    const shopItems = [
      { category: 'Shop', items: [
        { text: 'Products', icon: <Store />, path: '/shop' },
        { text: 'Shopping Cart', icon: <ShoppingCart />, path: '/cart' },
      ]}
    ];
    
    const appointmentItems = [
      { category: 'Appointments', items: [
        ...(user?.role === 'CLIENT' 
          ? [{ text: 'Book Appointment', icon: <Add />, path: '/book-appointment' }] 
          : []),
        { text: user?.role === 'CLIENT' ? 'My Appointments' : 'Appointments', icon: <CalendarToday />, path: '/appointments' },
        ...(user?.role !== 'CLIENT' ? [{ text: 'Calendar', icon: <CalendarToday />, path: '/calendar' }] : []),
      ]}
    ];
    
    const managementItems = user?.role === 'CLIENT' ? [] : [
      { category: 'Management', items: [
        ...(user?.role === 'ADMIN' || user?.role === 'RECEPTIONIST' ? [
          { text: 'Clients', icon: <People />, path: '/clients' },
        ] : []),
        ...(user?.role === 'ADMIN' ? [
          { text: 'Stylists & Staff', icon: <Person />, path: '/stylists' },
        ] : []),
        { text: 'Staff Roster', icon: <Assessment />, path: '/staff-roster' },
      ]}
    ];
    
    const serviceItems = user?.role === 'CLIENT' ? [] : [
      { category: 'Services', items: [
        ...(user?.role === 'ADMIN' ? [
          { text: 'Services', icon: <ContentCut />, path: '/services' },
        ] : []),
      ]}
    ];
    
    const accountItems = user?.role === 'CLIENT' ? [] : [];
    
    const adminItems = user?.role === 'ADMIN' ? [
      { category: 'Administration', items: [
        { text: 'Product Admin', icon: <Store />, path: '/product-admin' },
        { text: 'Loyalty Rewards', icon: <Assessment />, path: '/loyalty-rewards' },
        { text: 'Payments', icon: <Payment />, path: '/payments' },
        { text: 'Reports', icon: <Assessment />, path: '/reports' },
        { text: 'System Logs', icon: <Assessment />, path: '/system-logs' },
        { text: 'Settings', icon: <Settings />, path: '/settings' },
        { text: 'Calendar Test', icon: <CalendarToday />, path: '/calendar-test' },
      ]}
    ] : [];
    
    // Combine all categories based on user role
    return {
      flatItems: baseItems,
      categories: [
        ...shopItems,
        ...appointmentItems,
        ...managementItems.filter(category => category.items.length > 0),
        ...serviceItems.filter(category => category.items.length > 0),
        ...accountItems,
        ...adminItems,
      ]
    };
  };

  const { flatItems, categories } = getMenuItems();

  const drawer = (
    <div>
      <Toolbar sx={{ background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              backgroundColor: '#FFFFFF',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#E91E63'
            }}
          >
            <ContentCut fontSize="large" />
          </Box>
          <Box>
            <Typography variant="h6" noWrap component="div" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
              {salonName}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Salon & Spa Management
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      
      {/* Main Navigation */}
      <Box sx={{ px: 1, py: 1 }}>
        {/* Base items */}
        <List sx={{ py: 0 }}>
          {flatItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => item.path === '/home' ? window.location.href = '/home.html' : navigate(item.path)}
                sx={{
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: location.pathname === item.path ? 'rgba(233, 30, 99, 0.08)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(233, 30, 99, 0.04)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: location.pathname === item.path ? '#E91E63' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{ 
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    color: location.pathname === item.path ? '#E91E63' : 'inherit'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        {/* Categorized items */}
        {categories.map((category) => (
          <Box key={category.category} sx={{ mt: 2, mb: 1 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                px: 2,
                py: 0.75,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'white',
                letterSpacing: '0.05em',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#AD1457',
                borderRadius: '4px',
                mb: 0.75,
              }}
            >
              {category.category === 'Shop' && <Store sx={{ mr: 0.75, fontSize: '1rem', color: 'white' }} />}
              {category.category === 'Appointments' && <CalendarToday sx={{ mr: 0.75, fontSize: '1rem', color: 'white' }} />}
              {category.category === 'Management' && <People sx={{ mr: 0.75, fontSize: '1rem', color: 'white' }} />}
              {category.category === 'Services' && <Spa sx={{ mr: 0.75, fontSize: '1rem', color: 'white' }} />}
              {category.category === 'Account' && <Person sx={{ mr: 0.75, fontSize: '1rem', color: 'white' }} />}
              {category.category === 'Administration' && <Settings sx={{ mr: 0.75, fontSize: '1rem', color: 'white' }} />}
              {category.category}
            </Typography>
            
            <List dense sx={{ py: 0 }}>
              {category.items.map((item) => (
                <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => item.path === '/home' ? window.location.href = '/home.html' : navigate(item.path)}
                    sx={{
                      py: 0.75,
                      pl: 3,
                      borderRadius: 1,
                      backgroundColor: location.pathname === item.path ? 'rgba(233, 30, 99, 0.08)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(233, 30, 99, 0.04)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: location.pathname === item.path ? '#E91E63' : 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      primaryTypographyProps={{ 
                        fontSize: '0.85rem',
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        color: location.pathname === item.path ? '#E91E63' : 'inherit'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box display="flex" alignItems="center" gap={2} sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFFFFF'
              }}
            >
              <ContentCut />
            </Box>
            <Box>
              <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
                {salonName} Salon
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Beauty & Wellness Management
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>
          </IconButton>
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}>
              <ListItemIcon><Person fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => { window.location.href = '/home.html'; handleProfileMenuClose(); }}>
              <ListItemIcon><Home fontSize="small" /></ListItemIcon>
              Home Page
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
